import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { ConfirmationService, MenuItem, PrimeIcons } from 'primeng/api';
import {
  BranchesCrudService,
  SelectedBranchService,
  SelectedWorkspaceService,
  TeamsCrudService,
} from '@tokenbox/services';
import { ButtonDirective, ButtonIcon, ButtonLabel } from 'primeng/button';
import { ReactiveFormsModule } from '@angular/forms';
import { TieredMenu } from 'primeng/tieredmenu';
import { Router } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { confirm } from '@tokenbox/utils';
import { environment } from '../../../../environments/environment.development';
import { showPermissionDeniedDialog } from '../../permission-denied-dialog/show-permission-denied-dialog';
import { DialogService } from 'primeng/dynamicdialog';
import { showComingSoonDialog } from '@tokenbox/components';

@Component({
  selector: 'dt-branch-selector',
  templateUrl: './branch-selector.component.html',
  styleUrls: ['./branch-selector.component.scss'],
  imports: [
    ReactiveFormsModule,
    TieredMenu,
    ButtonDirective,
    ButtonIcon,
    ButtonLabel,
    FaIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BranchSelectorComponent {
  protected readonly PrimeIcons = PrimeIcons;

  private router = inject(Router);
  private selectedWorkspaceService = inject(SelectedWorkspaceService);
  private branchesCrudService = inject(BranchesCrudService);
  private selectedBranchService = inject(SelectedBranchService);
  private teamsCrudService = inject(TeamsCrudService);
  private confirmationService = inject(ConfirmationService);
  private dialogService = inject(DialogService);

  menuItems = computed<MenuItem[]>(() => {
    const selectedWorkspaceId =
      this.selectedWorkspaceService.selectedWorkspaceId();
    const selectedBranchId = this.selectedBranchService.selectedBranchId();
    const allBranches = this.branchesCrudService.allBranches();
    const selectedBranch = allBranches.find(
      (branch) => branch.id === selectedBranchId,
    );
    const branchesOfSelectedWorkspace = allBranches.filter(
      (branch) => branch.workspace.id === selectedWorkspaceId,
    );
    return [
      {
        label: selectedBranch?.name ?? 'Select a branch',
        icon: PrimeIcons.SHARE_ALT,
        items: [
          ...branchesOfSelectedWorkspace.map(
            (branch) =>
              ({
                label: branch.name,
                icon: this.selectedBranchService.isSelected(branch)
                  ? PrimeIcons.CHECK_CIRCLE
                  : branch.name === 'main'
                    ? PrimeIcons.SHIELD
                    : PrimeIcons.CIRCLE,
                styleClass: this.selectedBranchService.isSelected(branch)
                  ? 'font-semibold'
                  : undefined,
                items: [
                  {
                    label: 'Checkout',
                    icon: PrimeIcons.ARROW_CIRCLE_RIGHT,
                    routerLink: `/workspaces/${selectedWorkspaceId}/branches/${branch.id}`,
                    disabled: branch.id === selectedBranch?.id,
                    styleClass: 'font-normal',
                  },
                  {
                    separator: true,
                  },
                  {
                    label: 'New branch...',
                    icon: PrimeIcons.PLUS,
                    command: () => this.createBranch(branch.id),
                    styleClass: 'font-normal',
                  },
                  {
                    separator: true,
                  },
                  {
                    label: 'Rename...',
                    icon: PrimeIcons.PENCIL,
                    command: () => this.renameBranch(branch.id, branch.name),
                    disabled: branch.name === 'main',
                    styleClass: 'font-normal',
                  },
                  {
                    label: 'Delete',
                    icon: PrimeIcons.TRASH,
                    command: () => this.deleteBranch(branch.id),
                    disabled: branch.name === 'main',
                    styleClass: 'font-normal',
                  },
                ],
              }) satisfies MenuItem,
          ),
        ],
      },
    ] satisfies MenuItem[];
  });

  async createBranch(sourceBranchId: string | null = null) {
    if (environment.production) {
      await showComingSoonDialog(this.dialogService)(
        'BRANCHING',
        `Branching will be supported in a future release.

      Get notified as soon as it's ready!`,
      );
      return;
    }

    const selectedWorkspace =
      this.selectedWorkspaceService.getSelectedWorkspace();
    const team = this.teamsCrudService
      .allTeams()
      .find((team) => team.id === selectedWorkspace.team.id);
    if (!team) {
      return;
    }
    if (
      this.branchesCrudService.allBranches().length >=
      team.plan.MAX_BRANCHES_PER_WORKSPACE
    ) {
      await showPermissionDeniedDialog(this.dialogService)(
        team,
        'Maximum number of branches reached for this workspace.',
        'Limit reached',
      );
      return;
    }

    const name = window.prompt('New branch name')?.trim();
    const workspaceId = this.selectedWorkspaceService.getSelectedWorkspaceId();
    if (name) {
      const newBranchId = await this.branchesCrudService.createBranch({
        name,
        sourceBranchId,
        workspaceId,
      });
      void this.router.navigateByUrl(
        `/workspaces/${workspaceId}/branches/${newBranchId}`,
      );
    }
  }

  async renameBranch(branchId: string, currentName: string) {
    const name = window.prompt('Rename branch', currentName)?.trim();
    if (name) {
      await this.branchesCrudService.updateBranch({
        id: branchId,
        name,
      });
    }
  }

  async deleteBranch(branchId: string) {
    const workspaceId = this.selectedWorkspaceService.getSelectedWorkspaceId();
    const selectedBranchId = this.selectedBranchService.selectedBranchId();
    if (
      await confirm(this.confirmationService)(
        'Really delete this branch?',
        'All changes on this branch will be lost. This cannot be undone.',
      )
    ) {
      await this.branchesCrudService.deleteBranch(branchId);
      if (branchId === selectedBranchId) {
        void this.router.navigateByUrl(`/workspaces/${workspaceId}`);
      }
    }
  }
}
